import { useEffect, useState } from "react";
import { Button, Checkbox, Flex, Form, Input } from "antd";
import { UserServices } from "../../services/user";
import { IMasterDataUser } from "../../interfaces/master-data";
import MultiSelectTag from "../multi-select-tag";
import { ITableViewsEditParams } from "../../interfaces/table-view";
import { useAuth } from "../../context/AuthContext";

interface CreateAndEditTableViewProps {
  onClose: (okTriggered?: boolean) => void;
  detail?: any | undefined;
  onSave: ({ name, sharedWith }: ITableViewsEditParams) => Promise<boolean>;
  isViewSystem?: boolean;
}

const CreateAndEditTableView: React.FC<CreateAndEditTableViewProps> = ({
  onClose,
  detail,
  onSave,
  isViewSystem = false,
}) => {
  const [form] = Form.useForm();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [users, setUsers] = useState<IMasterDataUser[]>([]);
  const { user } = useAuth();

  const fetchData = async () => {
    try {
      const response: any = await UserServices.getBasic();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    form.setFieldsValue(detail);
  }, [detail, form]);

  const onFinish = async (values: ITableViewsEditParams) => {
    try {
      setIsLoadingSubmit(true);
      const saveDone = await onSave({ ...values, ...(isViewSystem && {systemView: values?.systemView ? true : false}) });
      if (saveDone) {
        setTimeout(() => {
          onClose(true);
        }, 100);
      }
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        label="View Name"
        name="name"
        rules={[
          { required: true, message: "" },
          {
            validator: (_, value) =>
              value && value.trim() !== "" ? Promise.resolve() : Promise.reject("Complete this required field."),
          },
        ]}
      >
        <Input placeholder="" />
      </Form.Item>
      <Form.Item label="Share this view with" name="sharedWith">
        <MultiSelectTag
          allowClear
          showSearch
          placeholder="Select user"
          options={users
            .filter((item) => item.id !== user?.id)
            .map((item) => ({
              value: item.id,
              label: `${item.firstName} ${item.lastName}`,
              email: item.email,
            }))}
          filterOption={(input, option) => {
            const label = option?.label?.toString().toLowerCase() ?? "";
            const email = option?.email?.toLowerCase?.() ?? "";

            return label.includes(input.toLowerCase()) || email.includes(input.toLowerCase());
          }}
          optionRender={(option) => (
            <>
              <div role="img" aria-label={String(option.data.label)}>
                {option.data.label}
              </div>
              <div style={{ color: "var(--text-black-400)" }}>{option.data.email}</div>
            </>
          )}
        />
      </Form.Item>
      {isViewSystem && (
        <Form.Item label="" name="systemView" valuePropName="checked">
          <Checkbox>System View</Checkbox>
        </Form.Item>
      )}

      <Flex justify="end" gap={16} style={{ marginTop: 20 }}>
        <Button
          style={{ marginLeft: 8 }}
          onClick={() => onClose()}
          disabled={isLoadingSubmit}
          color="primary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={isLoadingSubmit}>
          Save
        </Button>
      </Flex>
    </Form>
  );
};

export default CreateAndEditTableView;
